import React, { useEffect, useState, useContext, useRef } from 'react';
import './App.scss';
import UI from './lib.r2/ui/ui-controls';
import BarcodeScanner from './BarcodeScanner';
import TextField from '@mui/material/TextField';
import Mark from "mark.js";

import { createTheme, ThemeProvider } from '@mui/material/styles';

import { State, Action } from './Provider';
import LoginPage from './pages/LoginPage';
import {
	AppLogo,
	StyleButton,
	LogoutButton,
	HomeButton,
	CloseButton,
	ButtonGroup,
	CheckButton,
	SearchResultList,
	SearchView,
	NetworkStatus,
	IconButton
} from './Assets';
import TeachInPage from './pages/TeachInPage';
import ScanLog from './ScanLog';
import ReleaseLog from './ReleaseLog';
import store from './Store';

const scaling = {
	"orientationSwitch"	: 1.0,
	"enabled"	: true,
	"landscape" : {
		"minWidth"	: 500,
		"maxWidth"	: 1200,
		"height"	: 500
	},
	"portrait" : {
		"width"		: 500,
		"minHeight"	: 500,
		"maxHeight"	: 1000
	}
}


// ============================================================================ App
export default function App() {
	const action = useContext(Action);
	const state = useContext(State);

	const versionInfo = store.use('versionInfo')

	const darkTheme = createTheme({
		palette: {
			mode: 'dark',
			primary: {
				main: state.mainColor,
			},
		},
		typography: {
			fontFamily: 'Arial',
			fontSize: 16,
		},
	});


	useEffect(() => {
		window.addEventListener('load', function() {
			window.history.pushState({}, '');	//> load
		})

		window.addEventListener('popstate', function() {
			window.history.pushState({}, '')	//> popstate
			action('goBack');		//start app with login-page
		})

		// ---skip browser history navigation
		window.history.pushState(null, null, window.location.href); 
		window.history.back();
		window.history.forward(); 
	}, [])


	if (!state.config) return <ErrorPage />

	return (
		<ThemeProvider theme={darkTheme}>
			<UI.ScaleView id='app' setup={state.config?.scaling || scaling}>
				<MainApp />

				{state.scanLogOpen && <ScanLog />}

				{state.releaseLogOpen && <ReleaseLog />}

			</UI.ScaleView>
			
			{state.config &&
				<div id='app-version' onClick={e => action('open-release', !state.releaseLogOpen)}>
					{'Version: ' + (global.appVersion || state.config.version)}
					<span>{versionInfo}</span>
					<span>{state.config.api.server}</span>
					{/* {state.warehouse && <span>{'Warehouse: ' + state.warehouse.name}</span>} */}
				</div>
			}
		</ThemeProvider>
	);
}


// ---------------------------------------------------------------------------- MainApp
const MainApp = e => {
	const action = useContext(Action);
	const state = useContext(State);

	return (
		state.isExit?
			<UI.Backdrop>
				<h4>{global.getLabel('exit', 'Anwendung beenden') + '?'}</h4>
				<div style={{display:'flex'}}>
					<StyleButton label='OK' onClick={e => action('exit-on')} />
					<StyleButton label={global.getLabel('cancel')} onClick={e => action('exit-off')} secondary />
				</div>
			</UI.Backdrop>
		:
		<MainPage />
	)
}


// ---------------------------------------------------------------------------- MainPage
const MainPage = e => {
	const action = useContext(Action);
	const state = useContext(State);

	const onFirstClick = e => {
		if (!state.isInit) return

		if (!global.isActivates){
			global.isActivates = true

			if (store.settings.source.cam.selected){	//---first user click: initialize the camera'
				store.initCamera()
			}
		}
	}
	
	return (
		<div id='main-page' onClick={onFirstClick}>
			{(state.mandate && state.selectedPage !== 'filter-page' && state.selectedPage !== 'teach-in-page') &&
				<AppLogo src={state.mandate.logo} />
			}

			<UI.PageView id='page-view' selectedIndex={state.selectedPage} automount={false} autounmount >
				<LoginPage		id='login-page'
					automount
					onLogin={(user) => action('login', user)}
					onExit={e => action('exit')}
					onConnect={e => action('connect')}
				/>
				<StartPage		id='start-page' />
				<ScannerPage	id='scanner-page' automount autounmount={false} />
				<SearchPage		id='search-page' />
				<FilterPage		id='filter-page' />
				<ClonePage		id='clone-page' />

				<TeachInPage	id='teach-in-page' />
				<CompletePage	id='complete-page' />

			</UI.PageView>

			<NetworkStatus onClick={e => action('open-scanLog', true)} />

			{!!state.message &&
				<UI.Dialog open={true} close={false}
					header={state.message.title}
					footer={!!state.message.buttons &&
						state.message.buttons.map((b, i) => 
							<StyleButton key={i} width={120} label={b.label} onClick={e => {
								action(b.action, b.payload);
							}} />
						)
					}>
					<div className='mse-error-message'>
						<div>{state.message.text || (typeof state.message === 'string' && state.message)}</div>
					</div>
				</UI.Dialog>
			}
		</div>
	)
}


// ---------------------------------------------------------------------------- ErrorPage
const ErrorPage = props => {
	const action = useContext(Action);
	const state = useContext(State);

	return (
		<UI.Backdrop>
			{state.isError ?
				<>
					<div>{'Fehler beim Laden der Daten!'}</div>
					<StyleButton label='wiederholen' onClick={e => action('reload')} />
				</>
				:
				<UI.Spinner color='#029AA9' />
			}
		</UI.Backdrop>
	)
}


// ---------------------------------------------------------------------------- StartPage
const StartPage = props => {
	const action = useContext(Action);
	const state = useContext(State);
	
	const settings = store.use('settings')

	const wh = state.mandate?.warehouses;


	useEffect(() => {
		// ---init StartPage
		if (wh?.length === 1 && !state.warehouse){	//auto select warehouse if only one
			action('select-warehouse', 0)
		}

		return () => { //---unmount StartPage
		}
	}, [])


	return (
		!state.isInit?
			<UI.Backdrop>
				{state.isError ?
					<>
						<div>{'Fehler beim Laden der Daten!'}</div>
						<StyleButton label='wiederholen' onClick={e => action('reload')} />
					</>
					:
					<UI.Spinner color='#029AA9' />
				}
			</UI.Backdrop>
		:
		<div id='start-page'>
			{(wh && !state.warehouse)?
				<div className='warehouse-list'>
					<div className='start-title'>{global.getLabel('warehouse_title', 'WAREHOUSE')}</div>
					<ButtonGroup
						className='warehouse-buttons'
						onSelect={e => action('select-warehouse', e)}
						source={wh.map(w => w.name)}
					/>
				</div>
				:
				state.isScanning ?
					state.message ?
						<UI.Backdrop title={state.message.text}>
							<StyleButton label='OK' onClick={e => action('scan-close')} />
						</UI.Backdrop>
						:
						<UI.Backdrop spinner title='Start Barcode-Scanner ...'>
							<StyleButton label={global.getLabel('cancel')} onClick={e => action('scan-close')} />
						</UI.Backdrop>
					:
					<div className='start-buttons'>
						<UI.IconButton className='navi-button left' icon='chevron_left'
							onClick={e => action('warehouse_remove')}
						/>

						<div className='start-title'>{global.getLabel('article_title', 'ARTICLE')}</div>

						<div className='start-buttons-bar'>

						{state.isConnected &&	//native app successfully connected (zebra barcode-scanner)
							<IconButton image={'assets/icons/Vector-1.png'}
								label={global.getLabel('scan-icon') + ' (barcode)'}
								onClick={e => action('scan-barcode', 'scan')}
							/>
						}
						{settings?.search?.enableBarcodeScan &&
							<IconButton image={'assets/icons/Vector-1.png'}
								label={global.getLabel('scan-icon') + (state.isConnected? ' (camera)' : '')}
								onClick={e => action('scan-start', 'scan')}
							/>
						}
						{settings?.search?.enableSearch &&
							<IconButton image={'assets/icons/Vector.png'}
								label={global.getLabel('search-icon')}
								onClick={e => action('search-open')}
							/>
						}
						</div>
					</div>
			}
			<LogoutButton />
		</div>
	)
}


// ---------------------------------------------------------------------------- ScannerPage
const ScannerPage = props => {
	const action = useContext(Action);
	const state = useContext(State);

	useEffect(() => {
		// ---init ScannerPage

		return () => {	//---unmount ScannerPage
		}
	}, [])


	// ------------------------------------------------------------------------ init
	return (
		<BarcodeScanner
			selected={props.selected}
			mode={state.mode}
			config={state.config.camera}
			onResult={(e, c) => action('scan-result', {gtin:e, canvas:c})}
			onPhoto={e => action('photo-complete', e)}
			onClose={e => action('scan-close')}
			onError={e => action('scan-error')}
			onInit={e => action('camera-init')}
			enabled={props.selected}
		/>
	)
}


// ---------------------------------------------------------------------------- SearchPage
const SearchPage = e => {
	const action = useContext(Action);
	const state = useContext(State);
	const bundles = state.warehouse?._bundles;
	const [found, setFound] = useState([]);
	const [value, setValue] = useState(state.searchString || '')
	const delay = useRef()

	// const settings = store.use('settings')


	useEffect(() =>{
		if (value.length) onSearch(value);
	}, [])


	useEffect(() =>{
		onSearch(value);
	}, [state.warehouse?._bundles])


	// ------------------------------------------------------------------------ onSearch
	const onSearch = e => {
		if (!bundles) return

		let str = e.toLowerCase();
		setValue(str);

		clearTimeout(delay.current)

		if (str.length < 3){
			state.searchString = '';
			return setFound([]);
		}

		delay.current = setTimeout(() => {
console.log('search for:', str, '...')
			clearTimeout(delay.current)

			state.searchString = str;
			let arr = str.split(' ');		//multiple search strings
			let _found = bundles;

			let count = 0
			arr.forEach(a => {				//for each search-string
				_found = _found.filter((b, i) => {
					if (count < 100 && b._search?.indexOf(a) > -1){	//result limited for 100 articles
						count ++
						return true
					}else{
						return false
					}
				}
					// b._search?.indexOf(a) > -1					//bundle name or gtins match
				)
			})

			if (_found.length === 0){
				_found = store.findGtinBundle(bundles, str)	
			}else{
				_found = _found.sort((a, b) => a.name > b.name? 1 : -1);
			}
console.log('found:', _found.length)

			setFound(_found);
	
		}, 200)
	}


	const onStart = e => {
		if (found.length) action('search-complete', found[0]);
	}

	if (!bundles || !found) return null

	return (
		<div id='_search-page'>
			<HomeButton />

			<div className='warehouse-label'>{state.warehouse?.name}</div>

			<div className='search-container' >
				<div className='search-input start'>
					<TextField
						label={global.getLabel('search-input')}
						style={{fontSize:24, width:'100%'}}
						inputProps={{
							style: {
								paddingTop: 10,
								paddingBottom: 10
							}
						}}
						onChange={e => onSearch(e.target.value)}
						value={value}
						onKeyPress={e => {if (e.key === 'Enter' && found.length) onStart()}}
						inputRef={input => input && input.focus()}
					/>

					<UI.IconButton
						className={'search-button' + (!found.length? ' disabled' : '')}
						image='assets/icons/magnifier.png'
						onClick={onStart}
					/>

					{value &&
						<CloseButton onClick={() => onSearch('')} size={20} width={50} height={50} />
					}

					{state.searchString !== '' &&
						<>
							<div className='search-info'>{
								(found.length === 0?
									global.getLabel('found-not', 'Kein Produkt gefunden') + '!' :
									global.getLabel('found-some', 'Produkte gefunden') + ': '
										+ found.length + ' / '
										// + state.warehouse._bundles.length
										+ state.warehouse._num_items
								)
							}</div>
						</>
					}
					{/* {(!!state.warehouse.allowCreateItem) && */}
					{/* {settings.allowCreateArticle && */}
					{state.warehouse.teachin.settings.allowCreateArticle &&
						<StyleButton
							style={{marginLeft:'0px'}}
							label={global.getLabel('new-article', 'neuer Artikel')}
							onClick={e => action('create-new')}
						/>
					}
				</div>

				<SearchResultList mark={state.searchString} source={found} onSelect={e => {if (e) action('search-complete', e)}} />
			</div>

			<LogoutButton />
		</div>
	)
}//)


// ---------------------------------------------------------------------------- FilterPage
const FilterPage = e => {
	const action = useContext(Action);
	const state = useContext(State);
	const [select, setSelect] = useState();
	const [incomplete, setIncomplete] = useState(false);
	const settings = store.use('settings')


	useEffect(() => {
		if (state.gtin){	//filter page called from scan-result
			let mark = new Mark('.mark');
			mark.unmark({
				done: () => mark.mark(state.gtin)
			});
		}
	}, [])


	if (!state.bundle || !state.warehouse) return null;


	return (
		<div id='_filter-page' >
			<div className='filter-header'>
				<div className='filter-title mark'>{global.getLabel('search-result') + (
					// state.gtin? ' für ' + state.gtin : ''
					state.searchString? ': ' + state.searchString : ''
	
				)}</div>
				<HomeButton />
				<CloseButton onClick={e => action('teach-in-close')} />
			</div>

			<div className='hbox'>
				<div className='vbox center filter-body'>
					{state.bundle?.attributes?.image_iso &&
						<img className='filter-image' src={state.bundle.attributes.image_iso} alt='' />
					}
					<div className='filter-label'>
						{state.bundle.name}
						{state.bundle.articleNo && <span>{' (' + state.bundle.articleNo + ')'}</span>}
					</div>

					<div className='filter-gtin mark'>
						{state.bundle.gtins.map((g, i) => <span key={i}>{g}</span>)}
					</div>

					{settings?.start?.enableIncompletePallet &&
						<div className='filter-check'>
							<div className='filter-check-item'>
								<div>{global.getLabel('carrier-incomplete')}</div>
								<CheckButton checked={incomplete} onClick={e => setIncomplete(e)} />
							</div>
						</div>
					}

					<div className='filter-buttons'>
						<StyleButton
							label={select?
								global.getLabel('copy_start_button', 'KOPIEREN & START')
								: global.getLabel('teach-in', 'START')
							}
							onClick={e => action('teach-in-select', [select, incomplete, false])}
						/>

						{settings?.start?.allowCreateVariant &&
							<StyleButton secondary
								label={global.getLabel('new-variant', 'new Variant')}
								onClick={e => action('teach-in-select', [select, incomplete, true])}
							/>
						}

						{/* {(!!state.warehouse.allowCreateItem) && */}
						{(!!state.warehouse.teachin.settings.allowCreateArticle) &&
							<StyleButton
								style={{marginTop:'0px'}}
								label={global.getLabel('new-article', 'neuer Artikel')}
								onClick={e => action('create-new')}
							/>
						}
					</div>
				</div>

				{settings?.start?.enableLikeBundleCopy &&
					<SearchView onSelect={e => setSelect(e)} />
				}
			</div>
		</div>
	)
}


// ---------------------------------------------------------------------------- ClonePage
const ClonePage = e => {
	const action = useContext(Action);
	const state = useContext(State);
	const [name, setName] = useState(state.bundle? state.bundle.name : '');

	if (!state.bundle) return null;


	return (
		<div id='_clone-page' >
			<div className='teach-in-header'>
				<HomeButton />
				<div className='teach-in-title'>{state.bundle.name}</div>
				<div className='teach-in-gtin'>{state.bundle.gtin}</div>
				<CloseButton onClick={e => action('teach-in-close')} />
			</div>

			<div className='vbox center filter-body'>
				<TextField
					label={state.bundle.id?
						global.getLabel('clone-name', 'Clone-Name')
						: global.getLabel('create-name', 'Bezeichnung neuer Artikel')}
					style={{fontSize:24, width:400, marginBottom:40}}
					inputProps={{
						style: {paddingTop:10, paddingBottom:10}
					}}
					value={name}
					onChange={e => setName(e.target.value)}
					autoFocus
				/>

				<StyleButton
					label={state.bundle._new? global.getLabel('create', 'erstellen') : global.getLabel('clone')}
					onClick={e => action('teach-in-clone', name)}
				/>
			</div>
		</div>
	)
}


// ---------------------------------------------------------------------------- CompletePage
const CompletePage = e => {
	const action = useContext(Action);
	const state = useContext(State);

	if (!state.bundle) return null;

	const saving = state.isSaved === 0? 'Saving ...': 'OK';
	const title = global.getLabel('teach-in-complete', 'Erfassung abgeschlossen');

	return (
		<div id='_complete-page' >
			<div className='result-title'>{title}</div>

			<div className='result-label'>{state.bundle.name}</div>
			<div className='result-gtin'>{state.bundle.gtin}</div>

			<StyleButton label={saving} onClick={e => {
				state.isSaved !== 0 && action('home')
			}} />

			{state.isSaved === 2 &&
				<div className='result-info'>{'Network Error!\nBundle cached locally'}</div>
			}
			{state.isSaved === 3 &&
				<div className='result-info'>{'Test bundles are not stored on the server!'}</div>
			}
			<HomeButton />
			<LogoutButton />
		</div>
	)
}
